/*
<summary>
   This functional component is all about rendering the Tool Management CSV
   import popup and submitting the selected file to the upload API.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IFormModalProps } from '../../../models/forms/IForm';
import { IImportToolManagement } from '../../../models/forms/IImportToolManagement';
import DefaultModal from '../../../shared-components/modal/default-modal';
import { formatMessage } from '../../../translations/format-message';
import ImportToolManagementForm from '../forms/import-tool-management-form';

/**
* This functional component renders the CSV import popup for Tool Management and
* submits the selected file to the upload service.
* @param props: Modal identifier and close handler
*/
const ImportToolManagement = (props: IFormModalProps) => {
  const initialValues: IImportToolManagement = { csvFile: null };
  const { toolManagementStore } = useStore();
  const { ToolManagementFileUploadService, fileUploadState, resetFileUploadState } =
    toolManagementStore;

  useEffect(() => {
    if (fileUploadState.success) {
      onModalClosed();
    }
  }, [fileUploadState.success]);

  useEffect(() => {
    if (fileUploadState.error) {
      if (fileUploadState.error === 'PartialSuccess') {
        toast.success(formatMessage('PartialSuccess'));
        onModalClosed();
      } else {
        toast.error(formatMessage(fileUploadState.error));
        resetFileUploadState();
      }
    }
  }, [fileUploadState.error]);

  const onSubmitForm = (values: IImportToolManagement) => {
    if (values.csvFile) {
      ToolManagementFileUploadService(
        Number(getTenantIdFromLocalStorage()),
        values.csvFile
      );
    }
  };

  const onModalClosed = () => {
    resetFileUploadState();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <DefaultModal
        id={props.id}
        size="sm"
        className="modal-sm fancy-modal"
        addHeader="import_tool_management"
        updateHeader="import_tool_management"
        onModalClosed={onModalClosed}
        isLoading={fileUploadState.inProgress}
      >
        <ImportToolManagementForm
          initialValues={initialValues}
          id={props.id}
          submitHandler={onSubmitForm}
          onModalClosed={onModalClosed}
          isLoading={fileUploadState.inProgress}
        />
      </DefaultModal>
    </React.Fragment>
  );
};

export default observer(ImportToolManagement);
